import React, { useState } from 'react';
import { Stream as StreamType, Task } from '../types';
import { usePreferences } from '../contexts/PreferencesContext';
import Phase from './Phase';

interface StreamProps {
  stream: StreamType;
  tasks: Task[];
  onAddTask: (task: Partial<Task>) => void;
  onEditStream?: (stream: StreamType) => void;
  onDeleteTask?: (taskId: string) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  onDeleteStream?: (streamId: string) => void;
}

const Stream: React.FC<StreamProps> = ({ stream, tasks, onAddTask, onEditStream, onDeleteTask, onUpdateTask, onDeleteStream }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { preferences } = usePreferences();

  const toggleCollapse = (e: React.MouseEvent) => {
    // Don't toggle if clicking on action buttons
    if (e.target !== e.currentTarget && (e.target as Element).closest('.stream-actions')) {
      return;
    }
    setIsCollapsed(!isCollapsed);
  };

  // Filter phases based on preferences
  const getVisiblePhases = () => {
    return stream.phases.filter(phase => {
      if (!preferences.showOnHold && phase.name.toLowerCase() === 'on hold') {
        return false;
      }
      return true;
    });
  };

  // Calculate task counts for each phase when collapsed
  const getTaskCounts = () => {
    const visiblePhases = getVisiblePhases().sort((a, b) => a.order - b.order);
    return visiblePhases.map(phase => {
      const phaseTaskCount = tasks.filter(task => task.phaseId === phase.id).length;
      return phaseTaskCount;
    });
  };

  const taskCounts = getTaskCounts();
  const taskCountDisplay = taskCounts.join(' > ');

  const handleDeleteStream = (e: React.MouseEvent) => {
    e.stopPropagation();
    const totalTasks = tasks.length;
    const message = totalTasks > 0 
      ? `Are you sure you want to delete "${stream.name}"? This will also delete ${totalTasks} task${totalTasks === 1 ? '' : 's'}.`
      : `Are you sure you want to delete "${stream.name}"?`;
      
    if (window.confirm(message)) {
      onDeleteStream?.(stream.id);
    }
  };

  return (
    <div className={`stream ${isCollapsed ? 'collapsed' : ''}`}>
      <div 
        className="stream-header"
        style={{ backgroundColor: stream.color }}
        onClick={toggleCollapse}
      >
        <div className="stream-header-title">
          <button className="collapse-toggle">
            {isCollapsed ? '▶' : '▼'}
          </button>
          <span>
            {stream.name}
            {isCollapsed && ` - ${taskCountDisplay}`}
          </span>
        </div>
        {(onEditStream || onDeleteStream) && (
          <div className="stream-actions">
            {onEditStream && (
              <button
                className="edit-stream-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditStream(stream);
                }}
                title="Edit stream"
              >
                ✏️
              </button>
            )}
            {onDeleteStream && (
              <button
                className="delete-stream-btn"
                onClick={handleDeleteStream}
                title="Delete stream"
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>
      <div className={`stream-phases ${isCollapsed ? 'collapsed' : ''}`}>
        {getVisiblePhases()
          .sort((a, b) => a.order - b.order)
          .map(phase => (
            <Phase
              key={phase.id}
              phase={phase}
              tasks={tasks.filter(task => task.phaseId === phase.id)}
              streamId={stream.id}
              onAddTask={onAddTask}
              onDeleteTask={onDeleteTask}
              onUpdateTask={onUpdateTask}
            />
          ))}
      </div>
    </div>
  );
};

export default Stream;